import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search,
    Eye,
    Clock,
    ThumbsUp,
    ThumbsDown,
    Ban,
    Briefcase,
    User,
    X,
    Mail,
    Megaphone,
    XCircle,
    ClipboardList,
    CircleSlash,
    CheckCircle,
} from "lucide-react";

import "./Traitementdemandes.css";
import SourcingModal from "./SourcingModal";
import RecommandationCooptationModal from "./Recommandationcooptation";
import AnnonceModal from "./Annoncemodal";
import TraitementCandidatsModal from "./Traitementcandidatsmodal";
import "./Traitementcandidatsmodal.css";

import {
    fetchDemandesRecrutementRequest,
    approveDemandeRecrutementRequest,
    rejectDemandeRecrutementRequest,
    cancelDemandeRecrutementRequest,
} from "../../../../redux/actions/demandesRecrutementActions";

import { fetchEtablissementsRequest } from "../../../../redux/actions/etablissementsActions";

const ETAT_EN_ATTENTE = "En attente";
const ETAT_APPROUVEE = "Approuvée";
const ETAT_EN_COURS = "En cours";
const ETAT_REJETEE = "Rejetée";
const ETAT_ANNULEE = "Annulée";

const NIVEAUX_ETUDES = [
    "Diplôme (Bac + 2)",
    "Licence (Bac + 3)",
    "Master (Bac + 5)",
    "Ingénieur (Bac + 5)",
    "Doctorat et plus",
];
const ANNEES_EXPERIENCE = ["< 1 an", "> 1 an", "> 2 ans", "> 5 ans", "> 10 ans"];
const TYPES_EMPLOI = ["CDI", "CDD", "Intérim", "Freelance", "Autre"];

const TABS = [
    { id: 0, label: "En attente", icon: Clock, status: [ETAT_EN_ATTENTE] },
    { id: 1, label: "Approuvées", icon: ThumbsUp, status: [ETAT_APPROUVEE, ETAT_EN_COURS] },
    { id: 2, label: "Rejetées", icon: ThumbsDown, status: [ETAT_REJETEE] },
    { id: 3, label: "Annulées", icon: Ban, status: [ETAT_ANNULEE] },
];

const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};

const Modal = ({ open, title, children, footer, onClose, size = "md" }) => {
    if (!open) return null;
    const sizeClass = size === "lg" ? "tdModal__panel--lg" : "";
    return (
        <div className="tdModal" role="dialog" aria-modal="true" onMouseDown={onClose}>
            <div className={`tdModal__panel ${sizeClass}`} onMouseDown={(e) => e.stopPropagation()}>
                <div className="tdModal__header">
                    <div className="tdModal__title">{title}</div>
                    <button className="tdIconBtn" onClick={onClose} aria-label="Fermer"><X size={18} /></button>
                </div>
                <div className="tdModal__body">{children}</div>
                {footer && <div className="tdModal__footer">{footer}</div>}
            </div>
        </div>
    );
};

export default function TraitementDemandes() {
    const dispatch = useDispatch();

    const demandesState = useSelector((state) => state.demandesRecrutement || {});
    const demandes = demandesState.demandes || [];
    const demandesLoading = !!demandesState.loading;

    const etablissementsState = useSelector((state) => state.etablissements || {});
    const etablissements = etablissementsState.etablissements || [];

    const [activeTab, setActiveTab] = useState(0);

    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);

    const [showSourcingModal, setShowSourcingModal] = useState(false);
    const [selectedDemandeForSourcing, setSelectedDemandeForSourcing] = useState(null);

    const [showRecCoopModal, setShowRecCoopModal] = useState(false);
    const [selectedDemandeForRecCoop, setSelectedDemandeForRecCoop] = useState(null);

    const [showAnnonceModal, setShowAnnonceModal] = useState(false);
    const [selectedDemandeForAnnonce, setSelectedDemandeForAnnonce] = useState(null);

    const [showTraitementCandidatsModal, setShowTraitementCandidatsModal] = useState(false);
    const [selectedDemandeForTraitement, setSelectedDemandeForTraitement] = useState(null);

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [motif, setMotif] = useState("");
    const [decisionDemande, setDecisionDemande] = useState(null);

    const [filters, setFilters] = useState({
        poste: "",
        niveauEtude: "",
        ecole: "",
        anneeExperience: "",
        typeEmploi: "",
        status: "",
    });

    useEffect(() => {
        dispatch(fetchDemandesRecrutementRequest());
        dispatch(fetchEtablissementsRequest());
    }, [dispatch]);

    useEffect(() => {
        const isAnyModalOpen =
            showViewModal ||
            showSourcingModal ||
            showRecCoopModal ||
            showAnnonceModal ||
            showTraitementCandidatsModal ||
            showRejectModal ||
            showCancelModal;

        document.body.style.overflow = isAnyModalOpen ? "hidden" : "unset";
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [
        showViewModal,
        showSourcingModal,
        showRecCoopModal,
        showAnnonceModal,
        showTraitementCandidatsModal,
        showRejectModal,
        showCancelModal,
    ]);

    const getEtablissementName = (id, fallback) => {
        const etab = (etablissements || []).find((e) => Number(e.id) === Number(id));
        return etab ? (etab.abbreviation || etab.nom) : (fallback || "—");
    };

    const filteredDemandes = useMemo(() => {
        const currentTab = TABS[activeTab];
        let filtered = (demandes || []).filter((d) => currentTab.status.includes(d.statut || d.status));

        if (filters.poste) {
            filtered = filtered.filter((d) =>
                String(d.posteNom || d.poste || "")
                    .toLowerCase()
                    .includes(filters.poste.toLowerCase())
            );
        }

        if (filters.niveauEtude) {
            filtered = filtered.filter((d) => (d.niveauEtude || d.niveauxEtudes) === filters.niveauEtude);
        }

        if (filters.ecole) {
            filtered = filtered.filter((d) =>
                (d.etablissementNom || getEtablissementName(d.etablissementId, d.etabliseement)) === filters.ecole
            );
        }

        if (filters.anneeExperience) {
            filtered = filtered.filter((d) => (d.anneesExperience || "") === filters.anneeExperience);
        }

        if (filters.typeEmploi) {
            filtered = filtered.filter((d) => (d.typeEmploi || "") === filters.typeEmploi);
        }

        if (filters.status && activeTab === 1) {
            filtered = filtered.filter((d) => (d.statut || d.status) === filters.status);
        }

        return filtered;
    }, [demandes, activeTab, filters, etablissements]);

    const handleFilterChange = (field, value) =>
        setFilters((prev) => ({ ...prev, [field]: value }));

    const resetFilters = () =>
        setFilters({
            poste: "",
            niveauEtude: "",
            ecole: "",
            anneeExperience: "",
            typeEmploi: "",
            status: "",
        });

    const isAnyFilterFilled = Object.values(filters).some((val) => val !== "");

    const openView = (d) => {
        setSelectedDemande(d);
        setShowViewModal(true);
    };
    const closeView = () => {
        setShowViewModal(false);
        setSelectedDemande(null);
    };

    const openSourcing = (d) => {
        if (activeTab !== 1) return;
        setSelectedDemandeForSourcing(d);
        setShowSourcingModal(true);
    };
    const closeSourcing = () => {
        setShowSourcingModal(false);
        setSelectedDemandeForSourcing(null);
    };

    const openRecCoop = (d) => {
        if (activeTab !== 1) return;
        setSelectedDemandeForRecCoop(d);
        setShowRecCoopModal(true);
    };
    const closeRecCoop = () => {
        setShowRecCoopModal(false);
        setSelectedDemandeForRecCoop(null);
    };

    const openAnnonce = (d) => {
        if (activeTab !== 1) return;
        setSelectedDemandeForAnnonce(d);
        setShowAnnonceModal(true);
    };
    const closeAnnonce = () => {
        setShowAnnonceModal(false);
        setSelectedDemandeForAnnonce(null);
    };

    const openTraitementCandidats = (d) => {
        if (activeTab !== 1) return;
        setSelectedDemandeForTraitement(d);
        setShowTraitementCandidatsModal(true);
    };
    const closeTraitementCandidats = () => {
        setShowTraitementCandidatsModal(false);
        setSelectedDemandeForTraitement(null);
    };

    const approveDemande = (demandeId) => {
        dispatch(approveDemandeRecrutementRequest(demandeId));
    };

    const openRejectModal = (demande) => {
        setDecisionDemande(demande);
        setMotif("");
        setShowRejectModal(true);
    };

    const closeRejectModal = () => {
        setShowRejectModal(false);
        setDecisionDemande(null);
        setMotif("");
    };

    const confirmReject = () => {
        if (!motif.trim() || !decisionDemande?.id) return;
        dispatch(rejectDemandeRecrutementRequest(decisionDemande.id, motif.trim()));
        closeRejectModal();
    };

    const openCancelModal = (demande) => {
        setDecisionDemande(demande);
        setMotif("");
        setShowCancelModal(true);
    };

    const closeCancelModal = () => {
        setShowCancelModal(false);
        setDecisionDemande(null);
        setMotif("");
    };

    const confirmCancel = () => {
        if (!motif.trim() || !decisionDemande?.id) return;
        dispatch(cancelDemandeRecrutementRequest(decisionDemande.id, motif.trim()));
        closeCancelModal();
    };

    const getStatusClass = (status) => {
        switch (status) {
            case ETAT_EN_ATTENTE: return "tdPill--waiting";
            case ETAT_APPROUVEE: return "tdPill--approved";
            case ETAT_EN_COURS: return "tdPill--inprogress";
            case ETAT_REJETEE: return "tdPill--rejected";
            case ETAT_ANNULEE: return "tdPill--cancelled";
            default: return "";
        }
    };

    return (
        <div className="tdPage">
            <div className="tdHeader">
                <div>
                    <h1>Traitement des demandes</h1>
                    <p>Suivez et gérez vos demandes de recrutement par statut</p>
                </div>
            </div>

            <div className="tdWrap">

                    <div className="tdTabs">
                        {TABS.map((tab) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    className={`tdTab ${activeTab === tab.id ? "tdTab--active" : ""}`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    <Icon size={16} />
                                    <span>{tab.label}</span>
                                </button>
                            );
                        })}
                    </div>

            </div>

            <div className="tdWrap">
                <div className="tdCard">
                    <div className="tdBody">
                        <div className="tdFilterSection">
                            <div className="tdFilterTitle"><Search size={16} />Filtre de recherche</div>
                            <div className="tdFilterGrid">
                                <div className="tdField">
                                    <label>Intitulé du poste</label>
                                    <input
                                        type="text"
                                        value={filters.poste}
                                        onChange={(e) => handleFilterChange("poste", e.target.value)}
                                        placeholder="Rechercher..."
                                    />
                                </div>
                                <div className="tdField">
                                    <label>Niveau d'étude</label>
                                    <select value={filters.niveauEtude} onChange={(e) => handleFilterChange("niveauEtude", e.target.value)}>
                                        <option value="">Tous</option>
                                        {NIVEAUX_ETUDES.map((n) => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </div>
                                <div className="tdField">
                                    <label>Établissement</label>
                                    <select value={filters.ecole} onChange={(e) => handleFilterChange("ecole", e.target.value)}>
                                        <option value="">Tous</option>
                                        {(etablissements || []).map((e) => (
                                            <option key={e.id} value={e.abbreviation || e.nom}>
                                                {e.abbreviation || e.nom}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="tdField">
                                    <label>Années d'expérience</label>
                                    <select value={filters.anneeExperience} onChange={(e) => handleFilterChange("anneeExperience", e.target.value)}>
                                        <option value="">Toutes</option>
                                        {ANNEES_EXPERIENCE.map((a) => <option key={a} value={a}>{a}</option>)}
                                    </select>
                                </div>
                                <div className="tdField">
                                    <label>Type d'emploi</label>
                                    <select value={filters.typeEmploi} onChange={(e) => handleFilterChange("typeEmploi", e.target.value)}>
                                        <option value="">Tous</option>
                                        {TYPES_EMPLOI.map((t) => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                {activeTab === 1 && (
                                    <div className="tdField">
                                        <label>Statut</label>
                                        <select value={filters.status} onChange={(e) => handleFilterChange("status", e.target.value)}>
                                            <option value="">Tous</option>
                                            <option value={ETAT_APPROUVEE}>{ETAT_APPROUVEE}</option>
                                            <option value={ETAT_EN_COURS}>{ETAT_EN_COURS}</option>
                                        </select>
                                    </div>
                                )}
                            </div>
                            <div className="tdFilterActions">
                                <button
                                    className={`tdBtn ${isAnyFilterFilled ? "tdBtn--warning" : "tdBtn--ghost"}`}
                                    type="button"
                                    onClick={resetFilters}
                                    disabled={!isAnyFilterFilled}
                                >
                                    Réinitialiser les filtres
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="tdWrap">
                <div className="tdCard">
                    <div className="tdBody">
                        <div className="tdTopRow">
                            <h2 className="tdTitle">{TABS[activeTab].label} ({filteredDemandes.length})</h2>
                        </div>
                        <div className="tdTableWrap">
                            <table className="tdTable">
                                <thead>
                                <tr>
                                    <th>Intitulé du poste</th>
                                    <th>Niveau d'études</th>
                                    <th>Établissement</th>
                                    <th>Années d'expérience</th>
                                    <th>Type d'emploi</th>
                                    <th>Statut</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {demandesLoading ? (
                                    <tr><td colSpan={7} className="tdEmptyCell">Chargement...</td></tr>
                                ) : filteredDemandes.length === 0 ? (
                                    <tr><td colSpan={7} className="tdEmptyCell">Aucune demande trouvée</td></tr>
                                ) : (
                                    filteredDemandes.map((demande) => {
                                        const status = demande.statut || demande.status;
                                        return (
                                            <tr key={demande.id}>
                                                <td>
                                                    <span className="tdInline">
                                                        <Briefcase size={14} />
                                                        <span className="tdStrong">{demande.posteNom || demande.poste || "—"}</span>
                                                    </span>
                                                </td>
                                                <td className="tdMuted">{demande.niveauEtude || demande.niveauxEtudes || "—"}</td>
                                                <td className="tdMuted">{demande.etablissementNom || getEtablissementName(demande.etablissementId, demande.etabliseement)}</td>
                                                <td className="tdMuted">{demande.anneesExperience || "—"}</td>
                                                <td className="tdMuted">{demande.typeEmploi || "—"}</td>
                                                <td>
                                                    <button className={`tdPill ${getStatusClass(status)}`} type="button" disabled>
                                                        {status}
                                                    </button>
                                                </td>
                                                <td className="is-right">
                                                    <div className="tdRowActions">
                                                        <button className="tdIconBtn" type="button" title="Voir" onClick={() => openView(demande)}>
                                                            <Eye size={16} />
                                                        </button>

                                                        {activeTab === 0 && (
                                                            <>
                                                                <button
                                                                    className="tdIconBtn tdIconBtn--approve"
                                                                    type="button"
                                                                    title="Approuver la demande"
                                                                    onClick={() => approveDemande(demande.id)}
                                                                >
                                                                    <CheckCircle size={16} />
                                                                </button>
                                                                <button
                                                                    className="tdIconBtn tdIconBtn--reject"
                                                                    type="button"
                                                                    title="Rejeter la demande"
                                                                    onClick={() => openRejectModal(demande)}
                                                                >
                                                                    <CircleSlash size={16} />
                                                                </button>
                                                            </>
                                                        )}

                                                        {activeTab === 1 && (
                                                            <>
                                                                <button className="tdIconBtn tdIconBtn--primary" type="button" title="Sourcer des candidats" onClick={() => openSourcing(demande)}>
                                                                    <User size={16} />
                                                                </button>
                                                                <button className="tdIconBtn tdIconBtn--traitement" type="button" title="Traitement des candidats" onClick={() => openTraitementCandidats(demande)}>
                                                                    <ClipboardList size={16} />
                                                                </button>
                                                                <button className="tdIconBtn tdIconBtn--success" type="button" title="Recommandation / Cooptation" onClick={() => openRecCoop(demande)}>
                                                                    <Mail size={16} />
                                                                </button>
                                                                <button className="tdIconBtn tdIconBtn--annonce" type="button" title="Générer / Publier une annonce" onClick={() => openAnnonce(demande)}>
                                                                    <Megaphone size={16} />
                                                                </button>
                                                                <button className="tdIconBtn tdIconBtn--cancel" type="button" title="Annuler la demande" onClick={() => openCancelModal(demande)}>
                                                                    <XCircle size={16} />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                open={showViewModal}
                title="Détails de demande de recrutement"
                onClose={closeView}
                size="lg"
                footer={<button className="tdBtn tdBtn--primary" type="button" onClick={closeView}>Fermer</button>}
            >
                {selectedDemande && (
                    <div className="tdDetailsContent">
                        <div className="tdDetailsSection">
                            <h3 className="tdDetailsTitle"><Briefcase size={16} />Informations de la demande</h3>
                            <div className="tdDetailsRow"><div className="tdDetailsLabel">+ Poste :</div><div className="tdDetailsValue">{selectedDemande.posteNom || selectedDemande.poste || "—"}</div></div>
                            <div className="tdDetailsRow"><div className="tdDetailsLabel">+ Profil recherché :</div><div className="tdDetailsValue">{selectedDemande.profilRecherche || selectedDemande.profileRecherche || "—"}</div></div>
                            <div className="tdDetailsRow"><div className="tdDetailsLabel">+ Niveau d'étude :</div><div className="tdDetailsValue">{selectedDemande.niveauEtude || selectedDemande.niveauxEtudes || "—"}</div></div>
                            <div className="tdDetailsRow"><div className="tdDetailsLabel">+ Universités/écoles préférées :</div><div className="tdDetailsValue">{selectedDemande.etablissementNom || getEtablissementName(selectedDemande.etablissementId, selectedDemande.etabliseement)}</div></div>
                            <div className="tdDetailsRow"><div className="tdDetailsLabel">+ Années d'expérience :</div><div className="tdDetailsValue">{selectedDemande.anneesExperience || "—"}</div></div>
                            <div className="tdDetailsRow"><div className="tdDetailsLabel">+ Type d'emploi :</div><div className="tdDetailsValue">{selectedDemande.typeEmploi || "—"}</div></div>
                            <div className="tdDetailsRow"><div className="tdDetailsLabel">+ Description :</div><div className="tdDetailsValue">{selectedDemande.description || "Aucune"}</div></div>
                            <div className="tdDetailsRow">
                                <div className="tdDetailsLabel">+ Statut :</div>
                                <div className="tdDetailsValue">
                                    <span className={`tdPill ${getStatusClass(selectedDemande.statut || selectedDemande.status)}`}>
                                        {selectedDemande.statut || selectedDemande.status}
                                    </span>
                                </div>
                            </div>
                            <div className="tdDetailsRow"><div className="tdDetailsLabel">+ Date de validation :</div><div className="tdDetailsValue">{formatDate(selectedDemande.dateValidation)}</div></div>
                            <div className="tdDetailsRow"><div className="tdDetailsLabel">+ Validateur :</div><div className="tdDetailsValue">{selectedDemande.validateurNom || "—"}</div></div>
                            <div className="tdDetailsRow"><div className="tdDetailsLabel">+ Motif :</div><div className="tdDetailsValue">{selectedDemande.motif || "—"}</div></div>
                            <div className="tdDetailsRow"><div className="tdDetailsLabel">+ Descriptif de poste :</div><div className="tdDetailsValue">{selectedDemande.descriptifPoste || selectedDemande.descriptifposte || "Aucun"}</div></div>
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                open={showRejectModal}
                title="Rejeter la demande"
                onClose={closeRejectModal}
                footer={
                    <>
                        <button className="tdBtn tdBtn--ghost" type="button" onClick={closeRejectModal}>Annuler</button>
                        <button className="tdBtn tdBtn--primary" type="button" onClick={confirmReject} disabled={!motif.trim()}>
                            Confirmer le rejet
                        </button>
                    </>
                }
            >
                <div className="tdField">
                    <label>Motif de rejet *</label>
                    <textarea
                        rows={5}
                        value={motif}
                        onChange={(e) => setMotif(e.target.value)}
                        placeholder="Saisir le motif du rejet..."
                    />
                </div>
            </Modal>

            <Modal
                open={showCancelModal}
                title="Annuler la demande"
                onClose={closeCancelModal}
                footer={
                    <>
                        <button className="tdBtn tdBtn--ghost" type="button" onClick={closeCancelModal}>Annuler</button>
                        <button className="tdBtn tdBtn--primary" type="button" onClick={confirmCancel} disabled={!motif.trim()}>
                            Confirmer l'annulation
                        </button>
                    </>
                }
            >
                <div className="tdField">
                    <label>Motif d'annulation *</label>
                    <textarea
                        rows={5}
                        value={motif}
                        onChange={(e) => setMotif(e.target.value)}
                        placeholder="Saisir le motif de l'annulation..."
                    />
                </div>
            </Modal>

            <SourcingModal open={showSourcingModal} onClose={closeSourcing} demande={selectedDemandeForSourcing} />
            <RecommandationCooptationModal open={showRecCoopModal} onClose={closeRecCoop} demande={selectedDemandeForRecCoop} />
            <AnnonceModal open={showAnnonceModal} onClose={closeAnnonce} demande={selectedDemandeForAnnonce} />
            <TraitementCandidatsModal open={showTraitementCandidatsModal} onClose={closeTraitementCandidats} demande={selectedDemandeForTraitement} />
        </div>
    );
}